import React, { FC } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { TREE_SIDEBAR_WIDTH } from '../sidebar/commonStyleSideBar';

const skeletonShimmer = keyframes`
	0% {
		background-position: 100% 0;
	}

	100% {
		background-position: -100% 0;
	}
`;

export const SkeletonBlock = styled.div<{
    $width?: string;
    $height?: string;
    $radius?: string;
    $round?: boolean;
    $margin?: string;
}>`
    display: block;
    width: ${(props) => props.$width ?? '100%'};
    height: ${(props) => props.$height ?? '1em'};
    margin: ${(props) => props.$margin ?? '0'};
    flex-shrink: 0;
    border-radius: ${(props) => (props.$round ? '50%' : (props.$radius ?? '4px'))};
    background: linear-gradient(
        90deg,
        ${(props) => props.theme.skeleton.backgroundColor} 0%,
        ${(props) => props.theme.skeleton.foregroundColor} 50%,
        ${(props) => props.theme.skeleton.backgroundColor} 100%
    );
    background-size: 200% 100%;
    animation: ${skeletonShimmer} 1.3s ease-in-out infinite;
`;

export const SkeletonStack = styled.div<{ $gap?: string; $align?: string; $width?: string; $flex?: string; $margin?: string }>`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.$gap ?? '10px'};
    align-items: ${(props) => props.$align ?? 'stretch'};
    width: ${(props) => props.$width ?? 'auto'};
    flex: ${(props) => props.$flex ?? 'initial'};
    margin: ${(props) => props.$margin ?? '0'};
`;

export const SkeletonRow = styled.div<{ $gap?: string; $align?: string; $justify?: string; $margin?: string }>`
    display: flex;
    align-items: ${(props) => props.$align ?? 'center'};
    justify-content: ${(props) => props.$justify ?? 'flex-start'};
    gap: ${(props) => props.$gap ?? '10px'};
    margin: ${(props) => props.$margin ?? '0'};
`;

const SkeletonTabsBar = styled.div<{ $margin?: string }>`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    width: fit-content;
    max-width: 100%;
    margin: ${(props) => props.$margin ?? '0 0 16px'};
    padding: 2px;
    overflow: hidden;
    background: #ececef;
    border-radius: 8px;
`;

const SkeletonTab = styled.div<{ $active?: boolean; $icon?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    min-width: ${(props) => (props.$icon ? '32px' : 'auto')};
    padding: ${(props) => (props.$icon ? '0 10px' : '0 14px')};
    border-radius: 6px;
    background: ${(props) => (props.$active ? '#ffffff' : 'transparent')};
    box-shadow: ${(props) => (props.$active ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none')};
`;

export const SkeletonTabs: FC<{
    tabWidths?: string[];
    activeIndex?: number;
    showOverflow?: boolean;
    $margin?: string;
}> = ({ tabWidths = ['84px', '72px', '96px'], activeIndex = 0, showOverflow = false, $margin }) => (
    <SkeletonTabsBar $margin={$margin}>
        {tabWidths.map((width, index) => (
            <SkeletonTab key={`${width}-${index}`} $active={index === activeIndex}>
                <SkeletonBlock $width={width} $height='13px' />
            </SkeletonTab>
        ))}
        {showOverflow && (
            <SkeletonTab $icon>
                <SkeletonBlock $width='16px' $height='16px' $radius='4px' />
            </SkeletonTab>
        )}
    </SkeletonTabsBar>
);

const SkeletonFiltersRowContainer = styled.div<{ $margin?: string }>`
    display: flex;
    align-items: flex-end;
    gap: 4px;
    flex-wrap: wrap;
    margin: ${(props) => props.$margin ?? '0 0 4px'};
`;

const SkeletonFiltersControls = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    flex-wrap: wrap;
`;

const SkeletonFilterControl = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const SkeletonFiltersRow: FC<{
    showSearch?: boolean;
    searchWidth?: string;
    searchLabelWidth?: string;
    controlWidths?: string[];
    controlLabelWidths?: Array<string | null>;
    controlHeights?: string[];
    controlHeight?: string;
    $margin?: string;
}> = ({
    showSearch = true,
    searchWidth = '300px',
    searchLabelWidth,
    controlWidths = [],
    controlLabelWidths = [],
    controlHeights = [],
    controlHeight = '38px',
    $margin
}) => (
    <SkeletonFiltersRowContainer $margin={$margin}>
        {showSearch && (
            <SkeletonFilterControl>
                {searchLabelWidth && <SkeletonBlock $width={searchLabelWidth} $height='11px' />}
                <SkeletonBlock $width={searchWidth} $height='38px' $radius='6px' />
            </SkeletonFilterControl>
        )}
        {controlWidths.length > 0 && (
            <SkeletonFiltersControls>
                {controlWidths.map((width, index) => {
                    const labelWidth = controlLabelWidths[index];

                    return (
                        <SkeletonFilterControl key={`${width}-${index}`}>
                            {labelWidth && <SkeletonBlock $width={labelWidth} $height='11px' />}
                            <SkeletonBlock $width={width} $height={controlHeights[index] ?? controlHeight} $radius='6px' />
                        </SkeletonFilterControl>
                    );
                })}
            </SkeletonFiltersControls>
        )}
    </SkeletonFiltersRowContainer>
);

export const SkeletonDivider = styled(SkeletonBlock).attrs({
    $height: '1px',
    $radius: '0'
})``;

export const SkeletonPageShell = styled.div<{ $padding?: string }>`
    width: 100%;
    min-width: 0;
    padding: ${(props) => props.$padding ?? '20px 0 48px'};
`;

export const SkeletonPageHeader = styled.div<{ $margin?: string }>`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    margin: ${(props) => props.$margin ?? '0 0 24px'};
    min-width: 0;

    @media (max-width: 700px) {
        flex-direction: column;
    }
`;

export const SkeletonSurface = styled.div<{
    $padding?: string;
    $radius?: string;
    $overflow?: string;
    $minHeight?: string;
}>`
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.$radius ?? '8px'};
    background: #ffffff;
    padding: ${(props) => props.$padding ?? '0'};
    overflow: ${(props) => props.$overflow ?? 'hidden'};
    min-height: ${(props) => props.$minHeight ?? 'auto'};
    min-width: 0;
`;

export const SkeletonResponsiveGrid = styled.div<{
    $columns?: string;
    $gap?: string;
    $tabletColumns?: string;
    $mobileColumns?: string;
}>`
    display: grid;
    grid-template-columns: ${(props) => props.$columns ?? 'repeat(3, minmax(0, 1fr))'};
    gap: ${(props) => props.$gap ?? '18px'};
    min-width: 0;

    @media (max-width: 1100px) {
        grid-template-columns: ${(props) => props.$tabletColumns ?? 'repeat(2, minmax(0, 1fr))'};
    }

    @media (max-width: 700px) {
        grid-template-columns: ${(props) => props.$mobileColumns ?? 'minmax(0, 1fr)'};
    }
`;

export const SkeletonLeftSidebarLayout = styled.div<{ $breakpoint?: string }>`
    display: flex;
    align-items: flex-start;
    gap: 24px;
    min-width: 0;

    @media (max-width: ${(props) => props.$breakpoint ?? '900px'}) {
        display: block;
    }
`;

export const SkeletonLeftSidebar = styled.aside<{ $breakpoint?: string; $padding?: string }>`
    flex: 0 0 ${TREE_SIDEBAR_WIDTH}px;
    width: ${TREE_SIDEBAR_WIDTH}px;
    min-width: ${TREE_SIDEBAR_WIDTH}px;
    box-sizing: border-box;
    background: transparent;
    padding: ${(props) => props.$padding ?? '0'};
    align-self: start;
    overflow: hidden;

    @media (max-width: ${(props) => props.$breakpoint ?? '900px'}) {
        display: none;
    }
`;

export const SkeletonLeftSidebarContent = styled.div`
    flex: 1;
    min-width: 0;
`;

export const SkeletonSideNavLayout = styled.div<{
    $sidebarWidth?: string;
    $gap?: string;
    $breakpoint?: string;
}>`
    display: grid;
    grid-template-columns: ${(props) => props.$sidebarWidth ?? '180px'} minmax(0, 1fr);
    gap: ${(props) => props.$gap ?? '24px'};
    min-width: 0;

    @media (max-width: ${(props) => props.$breakpoint ?? '900px'}) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

export const SkeletonSideNav = styled.aside<{ $gap?: string; $breakpoint?: string }>`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.$gap ?? '10px'};
    align-self: start;
    min-width: 0;

    @media (max-width: ${(props) => props.$breakpoint ?? '900px'}) {
        display: none;
    }
`;

export const SkeletonPageMenuLayoutContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 15px;
    height: 100%;
    min-width: 0;
`;

export const SkeletonPageMenuContentContainer = styled.div<{ $breakpoint?: string }>`
    flex: 1;
    min-width: 0;
    margin-left: 216px;

    @media (max-width: ${(props) => props.$breakpoint ?? '860px'}) {
        margin-left: 0;
    }
`;

const SkeletonPageMenuContainer = styled.aside<{ $breakpoint?: string }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    position: fixed;
    width: 200px;
    min-width: 200px;
    margin-top: 35px;
    height: fit-content;

    @media (max-width: ${(props) => props.$breakpoint ?? '860px'}) {
        display: none;
    }
`;

const SkeletonPageMenuItem = styled.div<{ $active?: boolean; $subItem?: boolean }>`
    width: 100%;
    padding: ${(props) => (props.$subItem ? '4px 9px 4px 37px' : '6px 9px 6px 25px')};
    border-radius: ${(props) => (props.$active ? '8px' : '0')};
    background: ${(props) => (props.$active ? 'rgb(0 0 0 / 5%)' : 'transparent')};
    box-sizing: border-box;
`;

export const SkeletonPageMenu: FC<{
    itemWidths?: string[];
    activeIndex?: number;
    subItemIndexes?: number[];
    $breakpoint?: string;
}> = ({ itemWidths = ['72px', '118px', '88px'], activeIndex = 0, subItemIndexes = [], $breakpoint }) => (
    <SkeletonPageMenuContainer $breakpoint={$breakpoint}>
        {itemWidths.map((width, index) => {
            const isSubItem = subItemIndexes.includes(index);

            return (
                <SkeletonPageMenuItem key={`${width}-${index}`} $active={index === activeIndex} $subItem={isSubItem}>
                    <SkeletonBlock $width={width} $height={isSubItem ? '11px' : '12px'} />
                </SkeletonPageMenuItem>
            );
        })}
    </SkeletonPageMenuContainer>
);

export const SkeletonFullscreenOverlay = styled.div<{ $background?: string; $zIndex?: number }>`
    position: fixed;
    inset: 0;
    background: ${(props) => props.$background ?? '#f5f7fa'};
    z-index: ${(props) => props.$zIndex ?? 20};
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

export const SkeletonFullscreenTopBar = styled.div<{ $padding?: string; $minHeight?: string }>`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: ${(props) => props.$padding ?? '12px 24px'};
    background: #ffffff;
    border-bottom: 1px solid #e2e8f0;
    min-height: ${(props) => props.$minHeight ?? '64px'};
    gap: 16px;
    min-width: 0;
`;

export const SkeletonFlexSpacer = styled.div`
    flex: 1;
    min-width: 0;
`;

export const SkeletonFullscreenContent = styled.div<{ $padding?: string }>`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${(props) => props.$padding ?? '24px'};
    min-width: 0;
    min-height: 0;
`;

export const SkeletonCanvasBlock = styled(SkeletonBlock)`
    max-width: 900px;
    max-height: 560px;
`;

export const SkeletonTable = styled(SkeletonSurface).attrs({
    $overflow: 'hidden'
})``;

export const SkeletonTableRow = styled.div<{
    $columns: string;
    $gap?: string;
    $padding?: string;
    $minHeight?: string;
    $hideLastBorder?: boolean;
    $mobileColumns?: string;
    $mobileBreakpoint?: string;
}>`
    display: grid;
    grid-template-columns: ${(props) => props.$columns};
    gap: ${(props) => props.$gap ?? '18px'};
    align-items: center;
    min-height: ${(props) => props.$minHeight ?? '58px'};
    padding: ${(props) => props.$padding ?? '14px 18px'};
    border-bottom: 1px solid ${(props) => props.theme.skeleton.backgroundColor};
    min-width: 0;

    ${(props) =>
        props.$hideLastBorder !== false &&
        css`
            &:last-child {
                border-bottom: 0;
            }
        `}

    ${(props) =>
        props.$mobileColumns &&
        css`
            @media (max-width: ${props.$mobileBreakpoint ?? '700px'}) {
                grid-template-columns: ${props.$mobileColumns};
            }
        `}
`;

export const SkeletonFooterBar = styled.div<{ $position?: 'fixed' | 'static'; $padding?: string }>`
    position: ${(props) => props.$position ?? 'fixed'};
    right: ${(props) => (props.$position === 'static' ? 'auto' : '0')};
    bottom: ${(props) => (props.$position === 'static' ? 'auto' : '0')};
    left: ${(props) => (props.$position === 'static' ? 'auto' : 'var(--app-sidebar-width, 0px)')};
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: ${(props) => props.$padding ?? '16px 48px'};
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
    background: #ffffff;

    @media (max-width: 700px) {
        padding: 16px 24px;
    }
`;
